import { useEffect, useState } from "react";
import { FileText, Download, Code2, ShieldCheck, Globe } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const audienceBadge = (a) => {
  if (/public/i.test(a)) return { icon: Globe, color: "text-[#E6E6E6] border-white/30" };
  return { icon: ShieldCheck, color: "text-[#E6C46E] border-[#C5A24A]/60" };
};

const formatBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function AdminDocumentation() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/documentation");
        setItems(data.items || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openWithAuth = async (url, filename, asBlobMime) => {
    try {
      const res = await api.get(url.replace(/^\/api/, ""), { responseType: "blob" });
      const blob = new Blob([res.data], { type: asBlobMime });
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = obj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(obj), 1500);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-documentation-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.4em] text-[#8E9AA6] mb-2">Operations · Knowledge Base</div>
        <h1 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight text-white">Documentation</h1>
        <p className="text-[#8E9AA6] text-sm mt-3 max-w-2xl leading-relaxed">
          Official Phantasm Limousine Service Inc. manuals. Each is generated from a Markdown source and rendered to a
          luxury obsidian-and-silver themed PDF. Public manuals are available to clients; admin manuals are gated to
          the operations team.
        </p>
      </div>

      {loading && (
        <div className="text-[#8E9AA6] text-xs uppercase tracking-[0.3em]" data-testid="docs-loading">Loading…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="border border-white/10 px-6 py-10 text-center text-[#8E9AA6] text-xs uppercase tracking-[0.3em]">
          No documents available.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {items.map((d) => {
          const { icon: AudIcon, color } = audienceBadge(d.audience);
          return (
            <article
              key={d.slug}
              data-testid={`doc-card-${d.slug}`}
              className="group relative backdrop-blur-xl bg-[#121214]/80 border border-white/10 hover:border-[#C5A24A]/60 transition-all flex flex-col"
            >
              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3 mb-5">
                  <FileText size={26} strokeWidth={1.3} className="text-[#C5A24A]" />
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[9px] uppercase tracking-[0.25em] border ${color}`}>
                    <AudIcon size={11} strokeWidth={1.5} /> {d.audience}
                  </span>
                </div>

                <h2 className="font-serif text-xl font-semibold text-white tracking-tight leading-snug">
                  {d.title}
                </h2>
                <p className="text-[#8E9AA6] text-sm leading-relaxed mt-3 flex-1">{d.subtitle}</p>

                <dl className="mt-6 pt-5 border-t border-white/10 grid grid-cols-2 gap-3 text-[10px] uppercase tracking-[0.2em] text-[#8E9AA6]">
                  <div>
                    <dt className="opacity-70 mb-1">Size</dt>
                    <dd className="text-[#E6E6E6] tracking-wider">{formatBytes(d.size_bytes)}</dd>
                  </div>
                  <div>
                    <dt className="opacity-70 mb-1">Updated</dt>
                    <dd className="text-[#E6E6E6] tracking-wider normal-case">{formatDate(d.updated_at)}</dd>
                  </div>
                </dl>

                <div className="flex gap-3 mt-6">
                  <button
                    data-testid={`doc-download-pdf-${d.slug}`}
                    onClick={() => openWithAuth(d.pdf_url, `phantasm-${d.slug}.pdf`, "application/pdf")}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-[#E6C46E] transition-colors px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase font-medium"
                  >
                    <Download size={13} /> Download PDF
                  </button>
                  <button
                    data-testid={`doc-view-md-${d.slug}`}
                    onClick={() => openWithAuth(d.md_url, `phantasm-${d.slug}.md`, "text/markdown")}
                    className="inline-flex items-center justify-center gap-2 border border-white/15 text-white hover:border-white px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase font-medium"
                    title="View Markdown source"
                  >
                    <Code2 size={13} /> .md
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
